// MonthGrid — календарная сетка 7×6 (пн-вс × ~6 недель) с событиями в каждой ячейке.
// Используется в CalendarPage (MonthView).
// R21.2: событие с dealId получает иконку-индикатор со ссылкой на /crm/deals/{dealId}.
import { Box, Stack, Typography, Paper, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LinkIcon from '@mui/icons-material/Link';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, isSameMonth, isSameDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { CalendarEvent } from '@/api/calendar';

const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

interface MonthGridProps {
  /** Месяц для отображения. */
  month: Date;
  /** События, попадающие в сетку (startAt внутри интервала month±buffer). */
  events: CalendarEvent[];
  /** Колбэк при клике на ячейку дня. */
  onDayClick?: (date: Date) => void;
  /** Колбэк при клике на событие. */
  onEventClick?: (event: CalendarEvent) => void;
}

/** Сетка календаря на месяц (7×6). */
export function MonthGrid({ month, events, onDayClick, onEventClick }: MonthGridProps): JSX.Element {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  function eventsForDay(day: Date): CalendarEvent[] {
    return events.filter((e) => isSameDay(new Date(e.startAt), day));
  }

  return (
    <Box data-testid="month-grid" sx={{ width: '100%' }}>
      {/* Header row: дни недели */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          mb: 0.5,
        }}
      >
        {WEEKDAY_LABELS.map((label) => (
          <Box
            key={label}
            sx={{
              textAlign: 'center',
              py: 1,
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      {/* Days grid: 7 columns × N rows */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          gridAutoRows: 'minmax(96px, auto)',
        }}
      >
        {days.map((day) => {
          const inMonth = isSameMonth(day, month);
          const dayEvents = eventsForDay(day);
          return (
            <Paper
              key={day.toISOString()}
              elevation={0}
              onClick={() => onDayClick?.(day)}
              sx={{
                p: 0.75,
                cursor: onDayClick ? 'pointer' : 'default',
                border: 1,
                borderColor: inMonth ? 'divider' : 'transparent',
                opacity: inMonth ? 1 : 0.4,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: inMonth ? 'text.primary' : 'text.disabled',
                }}
              >
                {format(day, 'd', { locale: ru })}
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5, overflow: 'hidden' }}>
                {dayEvents.slice(0, 3).map((e) => (
                  <Box
                    key={e.id}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onEventClick?.(e);
                    }}
                    sx={{
                      fontSize: '0.6875rem',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      cursor: onEventClick ? 'pointer' : 'default',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                    }}
                  >
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {e.title}
                    </Box>
                    {e.dealId ? (
                      <MuiLink
                        component={RouterLink}
                        to={`/crm/deals/${e.dealId}`}
                        aria-label="Сделка"
                        title="Сделка"
                        onClick={(ev) => ev.stopPropagation()}
                        sx={{ color: 'inherit', display: 'inline-flex', flexShrink: 0 }}
                      >
                        <LinkIcon sx={{ fontSize: 12 }} />
                      </MuiLink>
                    ) : null}
                  </Box>
                ))}
                {dayEvents.length > 3 ? (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                    +{dayEvents.length - 3}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
